import React, { useCallback, useState } from 'react';

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AlignIcon from '@sharedCR/coreDesignSystem/icons/AlignIcon';
import CopyButtonCDS from 'app/shared/componentsRestyled/coreDesignSystem/Buttons/CopyButtonCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { copyToClipboard } from 'utils/copyToClipboard';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { LOAD_PAYLOAD_LABEL, PayloadContentType } from './PayloadViewer.constants';
import {
  headerActionsStyles,
  headerRowStyles,
  preStyles,
  revealButtonStyles,
  wrapperStyles,
} from './PayloadViewer.styles';
import { formatPayloadText } from './PayloadViewer.utils';

const PayloadViewer = ({
  label,
  dataTestId,
  revealDataTestId = null,
  downloadName = '',
  contentMeta = null,
  onFetch = null,
}) => {
  const [blob, setBlob] = useState(null);
  const [text, setText] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);

  const contentType = _.get(contentMeta, 'type');

  const isCompleted = contentType === PayloadContentType.COMPLETED;
  const isFailedToUpload = contentType === PayloadContentType.FAILED_TO_UPLOAD;
  const isSkipped = contentType === PayloadContentType.SKIPPED;

  const isLoaded = !_.isNil(text);

  // Redaction fail-open (FH-2677): the producer stored the body WITHOUT redaction, so it may expose raw
  // SSN/account numbers. Keep it concealed — no fetch, no render — until an admin explicitly reveals it.
  const hasUnredactedSensitiveData = _.get(contentMeta, 'hasUnredactedSensitiveData', false);
  const isConcealed = isCompleted && hasUnredactedSensitiveData && !isRevealed;

  const onCopy = useCallback(() => copyToClipboard(text ?? ''), [text]);

  const onDownload = useCallback(() => {
    if (_.isNil(blob)) {
      return;
    }

    downloadOrShareBlob(blob, downloadName);
  }, [blob, downloadName]);

  // Payload bytes are fetched ONLY on an explicit click — never on mount. Eagerly fetching every
  // completed item's request+response blobs when the drawer opened was the big-group lag (FH-2916).
  const fetchContent = useCallback(() => {
    if (_.isNil(onFetch) || isLoading || isLoaded) {
      return;
    }

    setIsLoading(true);
    setError(null);

    onFetch()
      .then(fetchedBlob => {
        setBlob(fetchedBlob);

        return fetchedBlob.text();
      })
      .then(rawText => {
        setText(formatPayloadText(rawText));
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load content.');
        setIsLoading(false);
      });
  }, [onFetch, isLoading, isLoaded]);

  const onLoad = useCallback(() => {
    if (!isConcealed) {
      fetchContent();
    }
  }, [isConcealed, fetchContent]);

  // Revealing is the admin's explicit consent to see the unredacted body, so it loads it in the same click.
  const onReveal = useCallback(() => {
    setIsRevealed(true);
    fetchContent();
  }, [fetchContent]);

  if (isSkipped || _.isNil(contentMeta)) {
    return null;
  }

  return (
    <Box sx={wrapperStyles}>
      <Box sx={headerRowStyles}>
        <Typography variant="medium14">{label}</Typography>
        {isCompleted && !isConcealed && (
          <Box sx={headerActionsStyles}>
            {!isLoaded && (
              <Button
                data-testid={`${dataTestId}-load`}
                disabled={isLoading}
                size="small"
                variant="outlined"
                onClick={onLoad}
              >
                {LOAD_PAYLOAD_LABEL}
              </Button>
            )}
            {isLoaded && (
              <>
                <Tooltip title="Download">
                  <IconButton data-testid={dataTestId} size="small" onClick={onDownload}>
                    <AlignIcon />
                  </IconButton>
                </Tooltip>
                <CopyButtonCDS label="Copy" onClick={onCopy} />
              </>
            )}
          </Box>
        )}
      </Box>
      {isFailedToUpload && (
        <Typography color="error" variant="regular14">
          {_.get(contentMeta, 'errorMessage', 'Content was not uploaded.')}
        </Typography>
      )}
      {isCompleted && hasUnredactedSensitiveData && (
        <Typography color="warning.main" variant="regular14">
          Redaction could not be applied to this payload; it may contain unredacted sensitive data
          (e.g. SSNs or account numbers).{' '}
          {isRevealed ? 'Revealed — handle with care.' : 'Hidden by default.'}
        </Typography>
      )}
      {isConcealed && (
        <Button
          data-testid={revealDataTestId}
          sx={revealButtonStyles}
          variant="text"
          onClick={onReveal}
        >
          Reveal payload
        </Button>
      )}
      {isCompleted && !isConcealed && (isLoading || error || isLoaded) && (
        <Box component="pre" sx={preStyles}>
          {isLoading && 'Loading…'}
          {error}
          {!isLoading && !error && text}
        </Box>
      )}
    </Box>
  );
};

PayloadViewer.propTypes = {
  label: PropTypes.string.isRequired,
  dataTestId: PropTypes.string.isRequired,
  revealDataTestId: PropTypes.string,
  contentMeta: PropTypes.shape({
    errorMessage: PropTypes.string,
    type: PropTypes.string,
    hasUnredactedSensitiveData: PropTypes.bool,
  }),
  downloadName: PropTypes.string,
  onFetch: PropTypes.func,
};

export default PayloadViewer;
